import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shiftsAPI, medicsAPI, templatesAPI, exportAPI, contractsAPI, jobTypesAPI } from '../lib/api';
import { fmt } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Search,
  Users,
  Copy,
  Trash2,
  Download,
  MapPin,
  Clock,
  Calendar,
  Pencil,
  User,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { CreateShiftModal, TimeSelect } from '../components/schedule';
import { trackRecentView } from '../hooks/useRecentlyViewed';
import { useAuth } from '../context/AuthContext';
import { isValidPostcode, normalisePostcode, detectPostcodeCountry } from '../lib/postcode';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  confirmed: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  standby: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  completed: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const TYPE_STYLES = {
  regular: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  night: 'bg-indigo-500/15 text-indigo-400',
  standby: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  emergency: 'bg-red-500/15 text-red-400',
};

export default function Shifts() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shifts, setShifts] = useState([]);
  const [medics, setMedics] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Initialize status filter from URL params (e.g., ?status=pending)
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlStatus = searchParams.get('status');
    return urlStatus || 'all';
  });
  const [typeFilter, setTypeFilter] = useState('all');
  // Track if we need to filter for today's shifts from URL param
  const [dateFilter, setDateFilter] = useState(() => {
    const urlFilter = searchParams.get('filter');
    return urlFilter || 'all';
  });
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [bulkCreateData, setBulkCreateData] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    days: 7,
    template_id: '',
    location: '',
    address: '',
    postcode: '',
    contract_id: '',
    job_type_id: '',
  });

  const [bulkAssignMedic, setBulkAssignMedic] = useState('');

  // Single-shift create (mirrors the Schedule page so you can create a
  // shift from the Shifts page too — same modal, same payload shape).
  const [showCreateShiftModal, setShowCreateShiftModal] = useState(false);
  // Edit Shift modal reuses the same CreateShiftModal component — when
  // ``editingShift`` is non-null the modal is in edit mode (iter71.5).
  const [showEditShiftModal, setShowEditShiftModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [newShift, setNewShift] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    start_time: '08:00',
    end_time: '20:00',
    location: '',
    address: '',
    postcode: '',
    shift_type: 'regular',
    job_type_id: '',
    contract_id: '',
    medic_id: '',
    rate_payable: 0,
    rate_chargeable: 0,
    notes: '',
    long_duration_ack: false,
  });

  // Create + Edit Template share the same input layout. When
  // ``editingTemplateId`` is set the Save button calls PUT, else POST.
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    shift_type: 'regular',
    start_time: '08:00',
    end_time: '20:00',
    default_location: '',
    default_address: '',
    default_postcode: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.shift_type = typeFilter;

      const [shiftsRes, medicsRes, templatesRes, contractsRes, jobTypesRes] = await Promise.all([
        shiftsAPI.list(params),
        medicsAPI.list(),
        templatesAPI.list(),
        contractsAPI.list(),
        jobTypesAPI.list(),
      ]);

      setShifts(shiftsRes.data);
      setMedics(medicsRes.data);
      setTemplates(templatesRes.data);
      setContracts(contractsRes.data || []);
      setJobTypes(jobTypesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter shifts by today's date if dateFilter is 'today'
  const getTodayDateString = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  const filteredShifts = shifts.filter(shift => {
    const matchesSearch = shift.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (shift.medic_name && shift.medic_name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Apply date filter if 'today' is specified
    if (dateFilter === 'today') {
      const todayStr = getTodayDateString();
      return matchesSearch && shift.date === todayStr;
    }
    
    return matchesSearch;
  });

  const handleSelectShift = (shiftId) => {
    setSelectedShifts(prev => 
      prev.includes(shiftId) 
        ? prev.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  const handleSelectAll = () => {
    if (selectedShifts.length === filteredShifts.length) {
      setSelectedShifts([]);
    } else {
      setSelectedShifts(filteredShifts.map(s => s.id));
    }
  };

  // Fresh blank state for the Add Shift modal. Shared with the Edit
  // modal reset so switching between edit and create cannot leak the
  // previous shift's postcode/location/contract into a new shift
  // (iter72 — reported by testing agent).
  const resetNewShift = useCallback(() => {
    setNewShift({
      date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
      start_time: '08:00',
      end_time: '20:00',
      location: '',
      address: '',
      postcode: '',
      shift_type: 'regular',
      job_type_id: '',
      contract_id: '',
      medic_id: '',
      rate_payable: 0,
      rate_chargeable: 0,
      notes: '',
      long_duration_ack: false,
    });
  }, []);

  const handleCreateShift = async () => {
    if (!newShift.contract_id || !newShift.job_type_id) {
      toast.error('Please pick both a Contract and a Job Type before creating a shift.');
      return;
    }
    if (!newShift.postcode) {
      toast.error('Please enter a postcode so the shift can be geofenced.');
      return;
    }
    const extendsBeyondStart = !!newShift.end_date && newShift.end_date > newShift.date;
    try {
      await shiftsAPI.create({
        ...newShift,
        end_date: extendsBeyondStart ? newShift.end_date : null,
        rate_payable: parseFloat(newShift.rate_payable) || 0,
        rate_chargeable: parseFloat(newShift.rate_chargeable) || 0,
        medic_id: newShift.medic_id || null,
        job_type_id: newShift.job_type_id,
        contract_id: newShift.contract_id,
        address: newShift.address || null,
        postcode: newShift.postcode || null,
      });
      toast.success(extendsBeyondStart ? 'Multi-day shift created' : 'Shift created successfully');
      setShowCreateShiftModal(false);
      resetNewShift();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  const handleBulkCreate = async () => {
    // iter72.3 — defensive: if a submission is in flight, ignore
    // further clicks. Prevents the duplicate-shift bug the user hit
    // when geocoding made the request look stuck.
    if (isSubmittingBulk) return;
    const template = templates.find(t => t.id === bulkCreateData.template_id);
    if (!template) {
      toast.error('Please select a template');
      return;
    }
    if (!bulkCreateData.contract_id) {
      toast.error('Please select a contract');
      return;
    }
    if (!bulkCreateData.job_type_id) {
      toast.error('Please select a job type');
      return;
    }
    // iter71.5 — bulk shifts share a single location, so the geocoder
    // needs a proper postcode anchor; a bare venue name like "Lola's
    // Care" fails every time. Fall back to the template's defaults
    // first so templates that already have an address keep working.
    const effectivePostcode = bulkCreateData.postcode || template.default_postcode || '';
    const effectiveAddress = bulkCreateData.address || template.default_address || '';
    const effectiveLocation = bulkCreateData.location || template.default_location || '';
    if (!effectivePostcode) {
      toast.error('Please enter a postcode so the shifts can be geocoded.');
      return;
    }
    if (!isValidPostcode(effectivePostcode)) {
      toast.error('Postcode must be a valid UK postcode (e.g. SW1A 1AA) or 5-digit French postcode (e.g. 75001).');
      return;
    }

    setIsSubmittingBulk(true);
    const toastId = toast.loading(`Creating ${bulkCreateData.days} shifts\u2026 geocoding the location can take a few seconds.`);
    try {
      const normalisedPostcode = normalisePostcode(effectivePostcode);
      const shiftsToCreate = [];
      for (let i = 0; i < bulkCreateData.days; i++) {
        const date = format(addDays(new Date(bulkCreateData.start_date), i), 'yyyy-MM-dd');
        shiftsToCreate.push({
          date,
          start_time: template.start_time,
          end_time: template.end_time,
          location: effectiveLocation,
          address: effectiveAddress || null,
          postcode: normalisedPostcode,
          shift_type: template.shift_type,
          contract_id: bulkCreateData.contract_id,
          job_type_id: bulkCreateData.job_type_id,
          rate_payable: 0,
          rate_chargeable: 0,
        });
      }

      await shiftsAPI.createBulk({ shifts: shiftsToCreate });
      toast.success(`${shiftsToCreate.length} shifts created successfully`, { id: toastId });
      setShowBulkCreateModal(false);
      fetchData();
    } catch (error) {
      // Pydantic 422 returns `detail` as a list of {loc, msg, type} objects
      // — stringify so sonner never receives a non-renderable child and
      // crashes the whole page to a blank screen (iter71.4).
      const d = error.response?.data?.detail;
      let msg = 'Failed to create shifts';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map(e => e.msg || JSON.stringify(e)).join('; ');
      toast.error(msg, { id: toastId });
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignMedic || selectedShifts.length === 0) {
      toast.error('Please select a medic and at least one shift');
      return;
    }

    try {
      await shiftsAPI.bulkAssign({
        shift_ids: selectedShifts,
        medic_id: bulkAssignMedic,
      });
      toast.success(`${selectedShifts.length} shifts assigned successfully`);
      setShowBulkAssignModal(false);
      setSelectedShifts([]);
      setBulkAssignMedic('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign shifts');
    }
  };

  const resetTemplateForm = () => {
    setEditingTemplateId(null);
    setNewTemplate({
      name: '',
      shift_type: 'regular',
      start_time: '08:00',
      end_time: '20:00',
      default_location: '',
      default_address: '',
      default_postcode: '',
    });
  };

  const handleOpenEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    trackRecentView(user?.id, {
      type: 'template',
      id: template.id,
      label: template.name || 'Untitled template',
      subtitle: `${template.start_time}–${template.end_time}`,
      href: '/shifts',
    });
    setNewTemplate({
      name: template.name || '',
      shift_type: template.shift_type || 'regular',
      start_time: template.start_time || '08:00',
      end_time: template.end_time || '20:00',
      default_location: template.default_location || '',
      default_address: template.default_address || '',
      default_postcode: template.default_postcode || '',
    });
    setShowTemplateModal(true);
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (newTemplate.default_postcode && !isValidPostcode(newTemplate.default_postcode)) {
      toast.error('Default postcode must be a valid UK (e.g. SW1A 1AA) or French (e.g. 75001) postcode.');
      return;
    }
    const payload = {
      ...newTemplate,
      default_postcode: newTemplate.default_postcode
        ? normalisePostcode(newTemplate.default_postcode)
        : '',
    };
    try {
      if (editingTemplateId) {
        await templatesAPI.update(editingTemplateId, payload);
        toast.success('Template updated successfully');
      } else {
        await templatesAPI.create(payload);
        toast.success('Template created successfully');
      }
      setShowTemplateModal(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      const d = error.response?.data?.detail;
      let msg = editingTemplateId ? 'Failed to update template' : 'Failed to create template';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map(e => e.msg || JSON.stringify(e)).join('; ');
      toast.error(msg);
    }
  };

  // Edit Shift reuses the existing CreateShiftModal (iter71.5). We seed
  // ``newShift`` with the current shift's values so the modal opens
  // pre-filled, then call PUT on save.
  const handleOpenEditShift = (shift) => {
    setEditingShift(shift);
    // iter72.5 — record on the user's Dashboard "Recently viewed" list.
    trackRecentView(user?.id, {
      type: 'shift',
      id: shift.id,
      label: `${shift.location || 'Shift'} · ${shift.date}`,
      subtitle: shift.medic_name || 'Unassigned',
      href: '/shifts',
    });
    setNewShift({
      date: shift.date || format(new Date(), 'yyyy-MM-dd'),
      end_date: shift.end_date || '',
      start_time: shift.start_time || '08:00',
      end_time: shift.end_time || '20:00',
      location: shift.location || '',
      address: shift.address || '',
      postcode: shift.postcode || '',
      shift_type: shift.shift_type || 'regular',
      job_type_id: shift.job_type_id || '',
      contract_id: shift.contract_id || '',
      medic_id: shift.medic_id || '',
      rate_payable: shift.rate_payable ?? 0,
      rate_chargeable: shift.rate_chargeable ?? 0,
      notes: shift.notes || '',
      long_duration_ack: true,
    });
    setShowEditShiftModal(true);
  };

  const handleUpdateShift = async () => {
    if (!editingShift) return;
    if (newShift.postcode && !isValidPostcode(newShift.postcode)) {
      toast.error('Postcode must be a valid UK postcode (e.g. SW1A 1AA) or 5-digit French postcode (e.g. 75001).');
      return;
    }
    try {
      await shiftsAPI.update(editingShift.id, {
        date: newShift.date,
        end_date: newShift.end_date || null,
        start_time: newShift.start_time,
        end_time: newShift.end_time,
        location: newShift.location,
        address: newShift.address || null,
        postcode: newShift.postcode ? normalisePostcode(newShift.postcode) : null,
        shift_type: newShift.shift_type,
        contract_id: newShift.contract_id,
        job_type_id: newShift.job_type_id,
        rate_payable: newShift.rate_payable,
        rate_chargeable: newShift.rate_chargeable,
        notes: newShift.notes,
        medic_id: newShift.medic_id || null,
      });
      toast.success('Shift updated successfully');
      setShowEditShiftModal(false);
      setEditingShift(null);
      resetNewShift();
      fetchData();
    } catch (error) {
      const d = error.response?.data?.detail;
      let msg = 'Failed to update shift';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map(e => e.msg || JSON.stringify(e)).join('; ');
      toast.error(msg);
    }
  };

  const handleCSVExport = async () => {
    setExportLoading(true);
    try {
      const baseUrl = exportAPI.getCSVUrl('shifts');
      const response = await fetch(baseUrl, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shifts_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await templatesAPI.delete(id);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDeleteShift = (shift) => {
    // Optimistic delete with 10-second Undo. Backend DELETE only fires after the timer expires.
    setShifts(prev => prev.filter(s => s.id !== shift.id));
    setSelectedShifts(prev => prev.filter(id => id !== shift.id));

    let undone = false;
    const commit = async () => {
      if (undone) return;
      try {
        await shiftsAPI.delete(shift.id);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete shift');
        fetchData();
      }
    };
    const timer = setTimeout(commit, 10000);

    toast('Shift deleted', {
      description: `${shift.date} · ${shift.location || 'no location'}`,
      duration: 10000,
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          clearTimeout(timer);
          setShifts(prev => prev.some(s => s.id === shift.id) ? prev : [...prev, shift]);
          toast.success('Deletion undone');
        },
      },
    });
  };

  const handleBulkDeleteShifts = () => {
    // Optimistic bulk delete with 10-second Undo (single toast, single server call).
    const idsToDelete = [...selectedShifts];
    if (idsToDelete.length === 0) return;
    const removed = shifts.filter(s => idsToDelete.includes(s.id));
    if (removed.length === 0) return;

    setShifts(prev => prev.filter(s => !idsToDelete.includes(s.id)));
    setSelectedShifts([]);

    let undone = false;
    const commit = async () => {
      if (undone) return;
      try {
        await shiftsAPI.bulkDelete(idsToDelete);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete shifts');
        fetchData();
      }
    };
    const timer = setTimeout(commit, 10000);

    toast(`${removed.length} shift${removed.length === 1 ? '' : 's'} deleted`, {
      description: 'Undo within 10 seconds to restore them.',
      duration: 10000,
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          clearTimeout(timer);
          setShifts(prev => {
            const existing = new Set(prev.map(s => s.id));
            const restored = removed.filter(s => !existing.has(s.id));
            return restored.length ? [...prev, ...restored] : prev;
          });
          toast.success(`Restored ${removed.length} shift${removed.length === 1 ? '' : 's'}`);
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shifts-container">
      <Tabs defaultValue="shifts" className="w-full">
        <TabsList className="bg-[#2B2B2B] border-white/5">
          <TabsTrigger value="shifts" className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]">
            Shifts
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]">
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="shifts" className="mt-6 space-y-4">
          {/* Shifts Header */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input
                placeholder="Search by location or medic..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#2B2B2B] border-white/10"
                data-testid="search-shifts-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 lg:w-[130px] bg-[#2B2B2B] border-white/10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="flex-1 lg:w-[130px] bg-[#2B2B2B] border-white/10">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="night">Night</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleCSVExport}
                disabled={exportLoading}
                className="flex-1 lg:flex-none border-[#1ABC9C] text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
                data-testid="export-shifts-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                className="flex-1 lg:flex-none border-white/10 text-[#F2F2F2]"
                onClick={() => setShowBulkCreateModal(true)}
                data-testid="bulk-create-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Bulk Create
              </Button>
              <Button
                className="flex-1 lg:flex-none bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                onClick={() => {
                  resetNewShift();
                  setShowCreateShiftModal(true);
                }}
                data-testid="create-shift-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Shift
              </Button>
              {selectedShifts.length > 0 && (
                <Button
                  className="flex-1 lg:flex-none bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                  onClick={() => setShowBulkAssignModal(true)}
                  data-testid="bulk-assign-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Assign ({selectedShifts.length})
                </Button>
              )}
              {selectedShifts.length > 0 && (
                <Button
                  variant="outline"
                  className="flex-1 lg:flex-none border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  onClick={handleBulkDeleteShifts}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedShifts.length})
                </Button>
              )}
            </div>
          </div>

          {/* Shifts Table (desktop) */}
          <Card className="bg-[#2B2B2B] border-white/5 hidden lg:block">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedShifts.length === filteredShifts.length && filteredShifts.length > 0}
                        onCheckedChange={handleSelectAll}
                        className="border-white/20"
                      />
                    </TableHead>
                    <TableHead className="text-[#A1A1AA]">Date</TableHead>
                    <TableHead className="text-[#A1A1AA]">Time</TableHead>
                    <TableHead className="text-[#A1A1AA]">Location</TableHead>
                    <TableHead className="text-[#A1A1AA]">Type</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                    <TableHead className="text-[#A1A1AA] text-right">Rates</TableHead>
                    <TableHead className="text-[#A1A1AA] text-right w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredShifts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-[#A1A1AA] py-8">
                        No shifts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredShifts.map((shift) => (
                      <TableRow 
                        key={shift.id} 
                        className={`border-white/5 hover:bg-white/5 ${shift.is_multi_day ? 'bg-indigo-500/5 border-l-4 border-l-indigo-400/60' : ''}`}
                        data-testid={`shift-row-${shift.id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedShifts.includes(shift.id)}
                            onCheckedChange={() => handleSelectShift(shift.id)}
                            className="border-white/20"
                          />
                        </TableCell>
                        <TableCell className="text-[#F2F2F2] font-medium">
                          {shift.is_multi_day && shift.end_date ? (
                            <div className="flex flex-col">
                              <span className="flex items-center gap-1.5">
                                {format(new Date(shift.date), 'MMM dd')}
                                <span className="text-indigo-300">→</span>
                                {format(new Date(shift.end_date), 'MMM dd, yyyy')}
                              </span>
                              <Badge
                                className="mt-1 w-fit bg-indigo-500/20 text-indigo-200 border border-indigo-400/40 text-[10px] font-semibold"
                                data-testid={`shift-multi-day-row-badge-${shift.id}`}
                              >
                                Multi-day · {(() => {
                                  const diff = Math.round((new Date(shift.end_date) - new Date(shift.date)) / (1000 * 60 * 60 * 24)) + 1;
                                  return `${diff} days`;
                                })()}
                              </Badge>
                            </div>
                          ) : (
                            format(new Date(shift.date), 'MMM dd, yyyy')
                          )}
                        </TableCell>
                        <TableCell className="text-[#A1A1AA] font-['JetBrains_Mono'] text-sm">
                          {shift.start_time} - {shift.end_time}
                        </TableCell>
                        <TableCell className="text-[#F2F2F2]">{shift.location}</TableCell>
                        <TableCell>
                          <Badge className={`${TYPE_STYLES[shift.shift_type]} border-0`}>
                            {shift.shift_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[shift.status]}>
                            {shift.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#F2F2F2]">
                          {shift.medic_name || <span className="text-[#A1A1AA]">Unassigned</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="text-sm">
                            <div className="text-[#1ABC9C]">{fmt(shift.rate_payable, shift.currency)}</div>
                            <div className="text-[#A1A1AA]">{fmt(shift.rate_chargeable, shift.currency)}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEditShift(shift)}
                              className="h-8 w-8 p-0 text-[#A1A1AA] hover:text-[#F2F2F2] hover:bg-white/5"
                              data-testid={`edit-shift-btn-${shift.id}`}
                              title="Edit shift"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteShift(shift)}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-shift-btn-${shift.id}`}
                              title="Delete shift"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Shifts Cards (mobile / tablet) */}
          <div className="lg:hidden space-y-3" data-testid="shifts-mobile-list">
            {filteredShifts.length === 0 ? (
              <Card className="bg-[#2B2B2B] border-white/5">
                <CardContent className="p-6 text-center text-[#A1A1AA]">
                  No shifts found
                </CardContent>
              </Card>
            ) : (
              filteredShifts.map((shift) => {
                const isSelected = selectedShifts.includes(shift.id);
                return (
                  <Card
                    key={shift.id}
                    className={`bg-[#2B2B2B] border-white/5 ${shift.is_multi_day ? 'border-l-4 border-l-indigo-400/60 bg-indigo-500/5' : ''}`}
                    data-testid={`shift-card-${shift.id}`}
                  >
                    <CardContent className="p-4 space-y-3">
                      {/* Top row: checkbox + date + status */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSelectShift(shift.id)}
                            className="border-white/20 mt-1"
                            data-testid={`shift-card-check-${shift.id}`}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-[#F2F2F2] font-medium">
                              <Calendar className="w-3.5 h-3.5 text-[#A1A1AA] shrink-0" />
                              {shift.is_multi_day && shift.end_date ? (
                                <span className="flex items-center gap-1 flex-wrap">
                                  {format(new Date(shift.date), 'MMM dd')}
                                  <span className="text-indigo-300">→</span>
                                  {format(new Date(shift.end_date), 'MMM dd, yyyy')}
                                </span>
                              ) : (
                                <span>{format(new Date(shift.date), 'MMM dd, yyyy')}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-sm text-[#A1A1AA] mt-1 font-['JetBrains_Mono']">
                              <Clock className="w-3 h-3 shrink-0" />
                              {shift.start_time} - {shift.end_time}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge className={STATUS_STYLES[shift.status]}>
                            {shift.status}
                          </Badge>
                          {shift.is_multi_day && shift.end_date && (
                            <Badge
                              className="bg-indigo-500/20 text-indigo-200 border border-indigo-400/40 text-[10px]"
                              data-testid={`shift-multi-day-card-badge-${shift.id}`}
                            >
                              {Math.round((new Date(shift.end_date) - new Date(shift.date)) / 86400000) + 1}d
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Location + type */}
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="w-3.5 h-3.5 text-[#A1A1AA] mt-0.5 shrink-0" />
                        <span className="text-[#F2F2F2] flex-1">{shift.location}</span>
                        <Badge className={`${TYPE_STYLES[shift.shift_type]} border-0 shrink-0`}>
                          {shift.shift_type}
                        </Badge>
                      </div>

                      {/* Medic */}
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-3.5 h-3.5 text-[#A1A1AA] shrink-0" />
                        <span className="text-[#F2F2F2]">
                          {shift.medic_name || <span className="text-[#A1A1AA] italic">Unassigned</span>}
                        </span>
                      </div>

                      {/* Footer: rates + edit + delete */}
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <div className="text-xs">
                          <span className="text-[#A1A1AA]">Pay </span>
                          <span className="text-[#1ABC9C] font-medium">{fmt(shift.rate_payable, shift.currency)}</span>
                          <span className="text-[#A1A1AA] ml-3">Charge </span>
                          <span className="text-[#F2F2F2] font-medium">{fmt(shift.rate_chargeable, shift.currency)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditShift(shift)}
                            className="h-8 w-8 p-0 text-[#A1A1AA] hover:text-[#F2F2F2] hover:bg-white/5"
                            data-testid={`shift-card-edit-${shift.id}`}
                            title="Edit shift"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteShift(shift)}
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`shift-card-delete-${shift.id}`}
                            title="Delete shift"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-6 space-y-4">
          {/* Templates Header */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-[#F2F2F2]">Shift Templates</h3>
            <Button
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              onClick={() => { resetTemplateForm(); setShowTemplateModal(true); }}
              data-testid="create-template-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.length === 0 ? (
              <Card className="bg-[#2B2B2B] border-white/5 col-span-full">
                <CardContent className="p-8 text-center text-[#A1A1AA]">
                  No templates created yet
                </CardContent>
              </Card>
            ) : (
              templates.map((template) => (
                <Card key={template.id} className="bg-[#2B2B2B] border-white/5">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#F2F2F2] text-base">{template.name}</CardTitle>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-[#A1A1AA] hover:text-[#F2F2F2] hover:bg-white/5"
                          onClick={() => handleOpenEditTemplate(template)}
                          data-testid={`edit-template-btn-${template.id}`}
                          title="Edit template"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Type:</span>
                        <Badge className={`${TYPE_STYLES[template.shift_type]} border-0`}>
                          {template.shift_type}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Time:</span>
                        <span className="text-[#F2F2F2] font-['JetBrains_Mono']">
                          {template.start_time} - {template.end_time}
                        </span>
                      </div>
                      {template.default_location && (
                        <div className="flex justify-between">
                          <span className="text-[#A1A1AA]">Location:</span>
                          <span className="text-[#F2F2F2]">{template.default_location}</span>
                        </div>
                      )}
                      {template.default_postcode && (
                        <div className="flex justify-between">
                          <span className="text-[#A1A1AA]">Postcode:</span>
                          <span className="text-[#F2F2F2] font-['JetBrains_Mono']">{template.default_postcode}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Bulk Create Modal */}
      <Dialog open={showBulkCreateModal} onOpenChange={(open) => {
        // iter72.3 — don't let the user dismiss the modal while a
        // bulk create is still in flight; otherwise they'll click
        // Create again and spawn duplicates.
        if (isSubmittingBulk && !open) return;
        setShowBulkCreateModal(open);
      }}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Bulk Create Shifts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Template</Label>
              <Select 
                value={bulkCreateData.template_id} 
                onValueChange={(value) => {
                  const t = templates.find(tt => tt.id === value);
                  setBulkCreateData(prev => ({
                    ...prev,
                    template_id: value,
                    // Prefill location / address / postcode from the
                    // template so admins only override when they want
                    // a different site (iter71.5).
                    location: prev.location || t?.default_location || '',
                    address: prev.address || t?.default_address || '',
                    postcode: prev.postcode || t?.default_postcode || '',
                  }));
                }}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="bulk-template-select">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.shift_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contract + Job Type are required by the backend
                (Contract → Job Type → Shift chain). Picking a contract
                filters the job type list below (iter71.4 fix). */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contract</Label>
                <Select
                  value={bulkCreateData.contract_id}
                  onValueChange={(value) => setBulkCreateData(prev => ({ ...prev, contract_id: value, job_type_id: '' }))}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="bulk-contract-select">
                    <SelectValue placeholder="Select contract" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    {contracts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Job Type</Label>
                <Select
                  value={bulkCreateData.job_type_id}
                  onValueChange={(value) => setBulkCreateData(prev => ({ ...prev, job_type_id: value }))}
                  disabled={!bulkCreateData.contract_id}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="bulk-jobtype-select">
                    <SelectValue placeholder={bulkCreateData.contract_id ? 'Select job type' : 'Pick a contract first'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2B2B2B] border-white/10">
                    {jobTypes
                      .filter(jt => jt.contract_id === bulkCreateData.contract_id)
                      .map(jt => (
                        <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={bulkCreateData.start_date}
                  onChange={(e) => setBulkCreateData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Number of Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={bulkCreateData.days}
                  onChange={(e) => setBulkCreateData(prev => ({ ...prev, days: parseInt(e.target.value) || 1 }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Site / Venue Name (Override)</Label>
              <Input
                value={bulkCreateData.location}
                onChange={(e) => setBulkCreateData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Leave empty to use template default"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="bulk-location-input"
              />
            </div>
            {/* Address + Postcode give Nominatim a reliable anchor so
                bulk shifts don't fall foul of the "venue name only"
                geocode miss (iter71.5). */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={bulkCreateData.address}
                  onChange={(e) => setBulkCreateData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Street + town"
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="bulk-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Postcode <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={bulkCreateData.postcode}
                  onChange={(e) => setBulkCreateData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                  placeholder="SW1A 1AA or 75001"
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="bulk-postcode-input"
                />
                {bulkCreateData.postcode && !isValidPostcode(bulkCreateData.postcode) && (
                  <p className="text-xs text-red-400">
                    Not a valid UK or French postcode.
                  </p>
                )}
                {bulkCreateData.postcode && isValidPostcode(bulkCreateData.postcode) && (
                  <p className="text-xs text-[#A1A1AA]">
                    Recognised as {detectPostcodeCountry(bulkCreateData.postcode)} postcode.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBulkCreateModal(false)} className="text-[#A1A1AA]" disabled={isSubmittingBulk}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkCreate}
              disabled={isSubmittingBulk}
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="bulk-create-submit-btn"
            >
              {isSubmittingBulk ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating {bulkCreateData.days} shifts…
                </span>
              ) : (
                <>Create {bulkCreateData.days} Shifts</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Modal */}
      <Dialog open={showBulkAssignModal} onOpenChange={setShowBulkAssignModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Assign {selectedShifts.length} Shifts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Medic</Label>
              <Select value={bulkAssignMedic} onValueChange={setBulkAssignMedic}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue placeholder="Select a medic" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {medics.map(medic => (
                    <SelectItem key={medic.id} value={medic.id}>
                      {medic.name} ({medic.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-[#A1A1AA]">
              This will assign all {selectedShifts.length} selected shifts to the chosen medic.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBulkAssignModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleBulkAssign} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Assign Shifts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Template Modal — shared form (iter71.5). The
          header + Save button swap labels based on ``editingTemplateId``. */}
      <Dialog
        open={showTemplateModal}
        onOpenChange={(open) => {
          setShowTemplateModal(open);
          if (!open) resetTemplateForm();
        }}
      >
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingTemplateId ? 'Edit Shift Template' : 'Create Shift Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Day Shift 12hr"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Shift Type</Label>
              <Select 
                value={newTemplate.shift_type} 
                onValueChange={(value) => setNewTemplate(prev => ({ ...prev, shift_type: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="night">Night</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <TimeSelect
                  value={newTemplate.start_time}
                  onChange={(v) => setNewTemplate(prev => ({ ...prev, start_time: v }))}
                  testIdPrefix="template-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <TimeSelect
                  value={newTemplate.end_time}
                  onChange={(v) => setNewTemplate(prev => ({ ...prev, end_time: v }))}
                  testIdPrefix="template-end-time"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Default Site / Venue Name</Label>
              <Input
                value={newTemplate.default_location}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, default_location: e.target.value }))}
                placeholder="e.g., Main Hospital"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="template-location-input"
              />
            </div>
            {/* Dedicated address + postcode so Bulk Create inherits a
                clean geocode anchor (iter71.5). */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Address (Optional)</Label>
                <Input
                  value={newTemplate.default_address}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, default_address: e.target.value }))}
                  placeholder="Street + town"
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="template-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Postcode (Optional)</Label>
                <Input
                  value={newTemplate.default_postcode}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, default_postcode: e.target.value.toUpperCase() }))}
                  placeholder="SW1A 1AA or 75001"
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="template-postcode-input"
                />
                {newTemplate.default_postcode && !isValidPostcode(newTemplate.default_postcode) && (
                  <p className="text-xs text-red-400">
                    Not a valid UK or French postcode.
                  </p>
                )}
                {newTemplate.default_postcode && isValidPostcode(newTemplate.default_postcode) && (
                  <p className="text-xs text-[#A1A1AA]">
                    Recognised as {detectPostcodeCountry(newTemplate.default_postcode)} postcode.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowTemplateModal(false); resetTemplateForm(); }} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" data-testid="save-template-btn">
              {editingTemplateId ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shift Modal — reuses the Schedule page modal so both entry
          points share the same single-day + optional multi-day flow. */}
      <CreateShiftModal
        open={showCreateShiftModal}
        onOpenChange={setShowCreateShiftModal}
        newShift={newShift}
        setNewShift={setNewShift}
        onSave={handleCreateShift}
        jobTypes={jobTypes}
        contracts={contracts}
        medics={medics}
      />

      {/* Edit Shift Modal — same component, PUT instead of POST
          (iter71.5). ``editingShift`` is cleared on close. */}
      <CreateShiftModal
        open={showEditShiftModal}
        onOpenChange={(open) => {
          setShowEditShiftModal(open);
          if (!open) {
            setEditingShift(null);
            // Prevent the Add Shift modal from inheriting the edited
            // shift's postcode/contract/etc. (iter72).
            resetNewShift();
          }
        }}
        newShift={newShift}
        setNewShift={setNewShift}
        onSave={handleUpdateShift}
        jobTypes={jobTypes}
        contracts={contracts}
        medics={medics}
        mode="edit"
      />
    </div>
  );
}
